// =====================================================================================
// INVRT Radar — web console — account — Team tab
//
// docs/API-CONTRACTS-C1.md §3 (api/routes/members.ts): the roster, invite, promote or
// demote, remove. The page shell registers it under the tab id "team".
//
// The roster is the server's own rows (name, email, role, joined date). The signed-in
// person is marked from the session's user id, never inferred from an email. Invite sends
// exactly `{email, is_org_admin}` (the server schema is `.strict()`), and the toast tells
// the truth the response carries: `invited: true` means an invitation email went out to
// an address with no Radar account yet; false means a known person was simply added.
//
// The LAST-ADMIN rule is the server's (409 `last_admin` on demoting or removing the only
// administrator); the console shows the mapped sentence in the confirm dialog rather than
// guessing the count itself. Every role change and removal confirms first. Reads are open
// to every member; the three mutations are offered to an organisation admin only.
// =====================================================================================

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { formatDate } from "@/lib/format";
import type {
  InviteMemberRequest,
  InviteMemberResponse,
  Member,
  MemberList,
  RemoveMemberResponse,
  UpdateMemberRequest,
  UpdateMemberResponse,
} from "@/lib/types.c1";

const membersQueryKey = (orgId: string | null) => ["members", orgId] as const;

/** How a row is named in controls and confirmations: the address, which every row has. */
function memberName(member: Member): string {
  return member.email ?? member.user_id;
}

// ---------------------------------------------------------------------------------------
// Invite
// ---------------------------------------------------------------------------------------

function InviteDialog({ onClose, orgName }: { onClose: () => void; orgName: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const { push } = useToast();
  const [email, setEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  const invite = useMutation({
    mutationFn: (body: InviteMemberRequest) =>
      api.post<InviteMemberResponse>("/members/invite", body),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: membersQueryKey(orgId) });
      if (response.invited) {
        push({
          tone: "success",
          title: "Invitation sent",
          body: `${response.email} has been emailed a link to join ${orgName}. They become a member the moment they sign in.`,
        });
      } else {
        push({
          tone: "success",
          title: "Added",
          body: `${response.email} already had a Radar account and is now a member of ${orgName}. No email was sent.`,
        });
      }
      onClose();
    },
  });

  const address = email.trim().toLowerCase();

  return (
    <Dialog
      open
      onClose={onClose}
      title="Invite someone to your team"
      description="A person with a Radar account already is added at once. Anyone else is emailed a sign-in link."
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={invite.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => invite.mutate({ email: address, is_org_admin: isAdmin })}
            loading={invite.isPending}
            disabled={!address.includes("@")}
          >
            Send invitation
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Email address"
          type="email"
          autoComplete="off"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="colleague@yourcompany.co.uk"
        />
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-line text-primary focus-visible:ring-2 focus-visible:ring-primary"
            checked={isAdmin}
            onChange={(event) => setIsAdmin(event.target.checked)}
          />
          <span>
            <span className="font-medium text-ink">Make this person an administrator</span>
            <span className="block text-xs text-ink-muted">
              Administrators manage sites, keys, billing, delivery and the team itself.
              Members can use Radar but cannot change its set-up.
            </span>
          </span>
        </label>
        {invite.isError ? <ErrorState error={invite.error} /> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------
// Role change / removal
// ---------------------------------------------------------------------------------------

function RoleDialog({ target, onClose }: { target: Member; onClose: () => void }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const { push } = useToast();
  const promoting = !target.is_org_admin;

  const update = useMutation({
    mutationFn: (body: UpdateMemberRequest) =>
      api.patch<UpdateMemberResponse>(`/members/${target.user_id}`, body),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: membersQueryKey(orgId) });
      push({
        tone: "success",
        title: "Role updated",
        body: `${memberName(target)} is now ${response.is_org_admin ? "an administrator" : "a member"}.`,
      });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={promoting ? "Make this person an administrator?" : "Make this person a member?"}
      description={memberName(target)}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => update.mutate({ is_org_admin: promoting })}
            loading={update.isPending}
          >
            {promoting ? "Make administrator" : "Make member"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-ink-muted">
        <p>
          {promoting
            ? "They will be able to manage sites, keys, billing, delivery and the team, including removing other administrators."
            : "They keep their access to Radar but can no longer change its set-up or manage the team. An organisation must keep at least one administrator."}
        </p>
        {update.isError ? <ErrorState error={update.error} /> : null}
      </div>
    </Dialog>
  );
}

function RemoveDialog({
  target,
  isSelf,
  onClose,
}: {
  target: Member;
  isSelf: boolean;
  onClose: () => void;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const { push } = useToast();

  const remove = useMutation({
    mutationFn: () => api.delete<RemoveMemberResponse>(`/members/${target.user_id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: membersQueryKey(orgId) });
      push({
        tone: "success",
        title: "Member removed",
        body: `${memberName(target)} no longer has access to this organisation.`,
      });
      onClose();
    },
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={isSelf ? "Leave this organisation?" : "Remove this member?"}
      description={memberName(target)}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={remove.isPending}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => remove.mutate()} loading={remove.isPending}>
            Remove member
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-ink-muted">
        <p>
          {isSelf
            ? "You lose access to this organisation at once. Another administrator can add you back."
            : "They lose access to this organisation at once. Their Radar account itself is untouched, and an administrator can add them back later."}
        </p>
        {remove.isError ? <ErrorState error={remove.error} /> : null}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------
// The panel
// ---------------------------------------------------------------------------------------

export function TeamPanel() {
  const api = useApiClient();
  const { orgId, activeMembership, session } = useSession();
  const isAdmin = activeMembership?.is_org_admin === true;
  const orgName = activeMembership?.organizations?.name ?? "your organisation";
  const selfId = session?.user.id ?? null;

  const [inviting, setInviting] = useState(false);
  const [changingRole, setChangingRole] = useState<Member | null>(null);
  const [removing, setRemoving] = useState<Member | null>(null);

  const members = useQuery({
    queryKey: membersQueryKey(orgId),
    queryFn: () => api.get<MemberList>("/members"),
    enabled: Boolean(orgId),
  });

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Team</CardTitle>
            <CardDescription className="mt-1">
              Everyone with access to {orgName}. Administrators manage its set-up; members
              use it.
            </CardDescription>
          </div>
          {isAdmin ? (
            <Button onClick={() => setInviting(true)}>
              <UserPlus className="h-4 w-4" aria-hidden="true" />
              Invite
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {!isAdmin ? (
            <p className="text-sm text-ink-muted">
              Only an organisation administrator can invite people, change roles or remove
              members.
            </p>
          ) : null}

          {members.isLoading ? <LoadingState label="Loading your team…" /> : null}
          {members.isError ? (
            <ErrorState error={members.error} onRetry={() => void members.refetch()} />
          ) : null}

          {members.isSuccess ? (
            members.data.members.length === 0 ? (
              <EmptyState
                title="No members yet"
                body="People who can sign in to this organisation appear here."
              />
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Name</TableHeaderCell>
                    <TableHeaderCell>Email</TableHeaderCell>
                    <TableHeaderCell>Role</TableHeaderCell>
                    <TableHeaderCell>Joined</TableHeaderCell>
                    {isAdmin ? (
                      <TableHeaderCell className="text-right">Actions</TableHeaderCell>
                    ) : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.data.members.map((member) => {
                    const isSelf = selfId !== null && member.user_id === selfId;
                    const name = memberName(member);
                    return (
                      <TableRow key={member.user_id}>
                        <TableCell className="font-medium">
                          <span className="flex flex-wrap items-center gap-2">
                            {member.full_name ?? <span className="text-ink-muted">—</span>}
                            {isSelf ? <Badge tone="neutral">You</Badge> : null}
                          </span>
                        </TableCell>
                        <TableCell className="text-ink-muted">{member.email ?? "—"}</TableCell>
                        <TableCell>
                          {member.is_org_admin ? (
                            <Badge tone="info">Admin</Badge>
                          ) : (
                            <Badge tone="neutral">Member</Badge>
                          )}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-ink-muted">
                          {formatDate(member.joined_at)}
                        </TableCell>
                        {isAdmin ? (
                          <TableCell className="text-right">
                            <span className="flex justify-end gap-2">
                              <Button
                                variant="secondary"
                                size="sm"
                                aria-label={
                                  member.is_org_admin
                                    ? `Make ${name} a member`
                                    : `Make ${name} an administrator`
                                }
                                onClick={() => setChangingRole(member)}
                              >
                                {member.is_org_admin ? "Make member" : "Make admin"}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-danger hover:text-danger"
                                aria-label={`Remove ${name}`}
                                onClick={() => setRemoving(member)}
                              >
                                Remove
                              </Button>
                            </span>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )
          ) : null}
        </CardContent>
      </Card>

      {inviting ? <InviteDialog orgName={orgName} onClose={() => setInviting(false)} /> : null}
      {changingRole ? (
        <RoleDialog target={changingRole} onClose={() => setChangingRole(null)} />
      ) : null}
      {removing ? (
        <RemoveDialog
          target={removing}
          isSelf={selfId !== null && removing.user_id === selfId}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </div>
  );
}
